import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useAutoLogout } from '../../hooks/useAutoLogout';
import { LoginForm } from './LoginForm';
import { LoadingSpinner } from './LoadingSpinner';
import { MfaGate } from './MfaGate';
import { SuperAdminProvider } from '../../contexts/SuperAdminContext';

interface AuthWrapperProps {
  children: React.ReactNode;
}

const LOGIN_EXIT_MS = 620;

export function AuthWrapper({ children }: AuthWrapperProps) {
  const { user, loading, sessionError } = useAuth();
  // 'login' shows the form; 'leaving' keeps it mounted while it slides apart;
  // 'authed' renders the app (behind the MFA gate).
  const [phase, setPhase] = useState<'login' | 'leaving' | 'authed'>('login');
  const exitTimerRef = useRef<number | null>(null);
  const hadUserRef = useRef(false);
  // Only animate the slide-away if the login form was actually shown
  // (a restored session on page load should not flash the form).
  const sawLoginFormRef = useRef(false);

  // Initialize auto-logout with 60 minute timeout
  useAutoLogout({
    timeoutMinutes: 60,
    showWarning: true,
    warningMinutes: 5
  });

  useEffect(() => {
    if (!user) {
      hadUserRef.current = false;
      sawLoginFormRef.current = true;
      if (exitTimerRef.current) {
        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      setPhase('login');
      return;
    }

    if (hadUserRef.current) return;
    hadUserRef.current = true;
    if (sawLoginFormRef.current) {
      setPhase((prev) => (prev === 'authed' ? 'authed' : 'leaving'));
      if (exitTimerRef.current) {
        window.clearTimeout(exitTimerRef.current);
      }
      exitTimerRef.current = window.setTimeout(() => {
        setPhase('authed');
        exitTimerRef.current = null;
      }, LOGIN_EXIT_MS);
    } else {
      setPhase('authed');
    }
  }, [user]);

  useEffect(() => {
    return () => {
      if (exitTimerRef.current) {
        window.clearTimeout(exitTimerRef.current);
      }
    };
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user || phase === 'login') {
    sawLoginFormRef.current = true;
    return <LoginForm sessionError={sessionError} />;
  }

  if (phase === 'leaving') {
    return (
      <LoginForm sessionError={sessionError} leaving />
    );
  }

  return (
    <MfaGate user={user}>
      <SuperAdminProvider>
        <div key="app-shell" className="animate-fade-in">
          {children}
        </div>
      </SuperAdminProvider>
    </MfaGate>
  );
}
